import React from 'react';

interface LiquidGlassProps {
  children: React.ReactNode;
  className?: string;
  blurIntensity?: 'sm' | 'md' | 'lg' | 'xl';
  bgOpacity?: 'low' | 'medium' | 'high' | 'ultra';
  glowColor?: 'white' | 'blue' | 'purple' | 'emerald' | 'black';
  variant?: 'default' | 'card' | 'modal' | 'nav' | 'button' | 'black-button';
  hover?: boolean;
}

export default function LiquidGlass({
  children,
  className = '',
  blurIntensity = 'md',
  bgOpacity = 'medium',
  glowColor = 'white',
  variant = 'default',
  hover = false
}: LiquidGlassProps) {
  const blurClasses = {
    sm: 'backdrop-blur-sm',
    md: 'backdrop-blur-md',
    lg: 'backdrop-blur-lg',
    xl: 'backdrop-blur-xl'
  };

  // Enhanced glass backgrounds with much stronger frosting effect
  const bgClasses = {
    low: 'bg-gradient-to-br from-white/80 via-white/75 to-white/70',
    medium: 'bg-gradient-to-br from-white/90 via-white/85 to-white/80',
    high: 'bg-gradient-to-br from-white/95 via-white/92 to-white/90',
    ultra: 'bg-gradient-to-br from-white/98 via-white/96 to-white/95'
  };

  // Enhanced borders with light refraction simulation
  const glowClasses = {
    white: 'border-white/50 shadow-[0_8px_32px_rgba(31,38,135,0.15),inset_0_1px_0_rgba(255,255,255,0.4),inset_0_-1px_0_rgba(255,255,255,0.2)]',
    blue: 'border-blue-200/70 shadow-[0_8px_32px_rgba(59,130,246,0.2),inset_0_1px_0_rgba(147,197,253,0.4),inset_0_-1px_0_rgba(147,197,253,0.2)]',
    purple: 'border-purple-200/70 shadow-[0_8px_32px_rgba(147,51,234,0.2),inset_0_1px_0_rgba(196,181,253,0.4),inset_0_-1px_0_rgba(196,181,253,0.2)]',
    emerald: 'border-emerald-200/70 shadow-[0_8px_32px_rgba(16,185,129,0.2),inset_0_1px_0_rgba(167,243,208,0.4),inset_0_-1px_0_rgba(167,243,208,0.2)]',
    black: ''
  };

  const variantClasses = {
    default: 'rounded-xl border-2',
    card: 'rounded-2xl border-2 shadow-[0_20px_50px_rgba(8,_112,_184,_0.07)]',
    modal: 'rounded-3xl border-2 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]',
    nav: 'rounded-lg border-2',
    button: 'rounded-lg border-2 transition-all duration-300',
    'black-button': 'rounded-md'
  };

  // Enhanced hover effects with more dramatic glass transformation
  const getHoverClasses = () => {
    if (!hover) return '';
    
    if (variant === 'black-button') {
      return '';
    }
    
    return 'hover:bg-gradient-to-br hover:from-white/80 hover:via-white/70 hover:to-white/60 hover:shadow-[0_12px_40px_rgba(31,38,135,0.25),inset_0_2px_0_rgba(255,255,255,0.6),inset_0_-2px_0_rgba(255,255,255,0.3)] hover:scale-[1.02] hover:border-white/60 transition-all duration-300 ease-out';
  };

  // Enhanced inner glow and depth
  const depthEffect = 'before:absolute before:inset-0 before:rounded-[inherit] before:bg-gradient-to-br before:from-white/20 before:via-transparent before:to-transparent before:pointer-events-none';

  // Special handling for black buttons
  if (variant === 'black-button') {
    return (
      <div
        className={`
          bg-black
          rounded-md
          ${className}
        `}
      >
        {children}
      </div>
    );
  }

  const backgroundClass = bgClasses[bgOpacity];

  return (
    <div
      className={`
        relative
        ${blurClasses[blurIntensity]}
        ${backgroundClass}
        ${glowClasses[glowColor]}
        ${variantClasses[variant]}
        ${getHoverClasses()}
        ${depthEffect}
        ${className}
      `}
      style={{
        background: `
          linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.1)),
          linear-gradient(225deg, rgba(255,255,255,0.15), rgba(255,255,255,0.1)),
          ${bgOpacity === 'low' ? 'rgba(255,255,255,0.8)' : 
            bgOpacity === 'medium' ? 'rgba(255,255,255,0.9)' : 
            bgOpacity === 'high' ? 'rgba(255,255,255,0.95)' :
            'rgba(255,255,255,0.98)'}
        `,
        backdropFilter: 'blur(30px) saturate(180%)',
        WebkitBackdropFilter: 'blur(30px) saturate(180%)',
      }}
    >
      {children}
    </div>
  );
}